import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useContent } from "../../i18n/useContent";
import Button from "../ui/Button";
import Icon from "../ui/Icons";
import Reveal from "../ui/Reveal";
import "./Contact.css";

const initialForm = { name: "", company: "", email: "", phone: "", message: "" };

function buildMailtoFallback(payload, contact) {
  const f = contact.mailFields;
  const lines = [`${f.name}: ${payload.name}`];
  if (payload.company) lines.push(`${f.company}: ${payload.company}`);
  if (payload.phone) lines.push(`${f.phone}: ${payload.phone}`);
  if (payload.email) lines.push(`${f.email}: ${payload.email}`);
  if (payload.message) lines.push(`${f.message}: ${payload.message}`);
  const body = lines.join("\n");
  return `mailto:${contact.email}?subject=${encodeURIComponent(contact.emailSubject)}&body=${encodeURIComponent(body)}`;
}

export default function Contact() {
  const { contact } = useContent();
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const canSubmit = formData.name.trim() !== "" && (formData.email.trim() !== "" || formData.phone.trim() !== "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || status === "sending") return;

    const payload = {
      name: formData.name.trim(),
      company: formData.company.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      message: formData.message.trim(),
    };

    setStatus("sending");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // `lang` lets the Offices team know which language to answer in.
        body: JSON.stringify({ ...payload, lang: i18n.resolvedLanguage || i18n.language }),
      });
      if (!res.ok) throw new Error("send failed");
      setStatus("sent");
      setFormData(initialForm);
    } catch {
      // Server-side send unavailable (e.g. Resend not configured yet, or a
      // network hiccup) — fall back to opening the visitor's own mail client
      // with everything pre-filled, so the request never just gets lost.
      window.location.href = buildMailtoFallback(payload, contact);
      setStatus("error");
    }
  };

  return (
    <section id="contacto" className="section">
      <div className="container">
        <div className="section-head center">
          <p className="eyebrow">{contact.eyebrow}</p>
          <h2 className="section-title">{contact.title}</h2>
          <p className="section-lead">{contact.lead}</p>
        </div>

        <div className="contact-grid">
          <Reveal className="contact-info">
            <div className="contact-info-block">
              <h3 className="contact-info-title">{contact.officeTitle}</h3>
              <span className="contact-member-links">
                <a
                  className="contact-member-link"
                  href={`mailto:${contact.email}`}
                  aria-label={t("a11y.emailOffice", { email: contact.email })}
                >
                  <Icon name="mail" size={18} />
                  <span>{contact.email}</span>
                </a>
              </span>
            </div>

            <div className="contact-info-block">
              <h3 className="contact-info-title">{contact.office.title}</h3>
              <address className="contact-office-address">
                <Icon name="mapPin" size={18} />
                <a
                  href={contact.office.mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t("a11y.viewOnGoogleMaps", { place: contact.office.title })}
                >
                  {contact.office.address}
                </a>
              </address>
              <a
                className="contact-office-map"
                href={contact.office.mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("a11y.viewOnMap", { place: contact.office.title })}
              >
                <span>{t("common.viewOnMap")}</span>
                <Icon name="arrowRight" size={16} />
              </a>
            </div>
          </Reveal>

          <Reveal className="contact-form-wrap" delay={0.1}>
            {status === "sent" ? (
              <div className="contact-success">
                <Icon name="check" size={32} />
                <p>{contact.form.success}</p>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="contact-field">
                  <label htmlFor="contact-name">{contact.form.name}</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-company">{contact.form.company}</label>
                  <input
                    id="contact-company"
                    name="company"
                    type="text"
                    autoComplete="organization"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>

                <div className="contact-field-row">
                  <div className="contact-field">
                    <label htmlFor="contact-email">{contact.form.email}</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="contact-field">
                    <label htmlFor="contact-phone">{contact.form.phone}</label>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-message">{contact.form.message}</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={4}
                    placeholder={contact.form.messagePlaceholder}
                    value={formData.message}
                    onChange={handleChange}
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  arrow
                  disabled={!canSubmit || status === "sending"}
                  className="contact-submit"
                >
                  {status === "sending" ? contact.form.sending : contact.form.submit}
                </Button>
                <p className="contact-disclaimer">
                  {status === "error" ? contact.form.error : contact.form.disclaimer}
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
